import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1767785990437-dfe1fe516fe8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGNoZWYlMjBjb29raW5nJTIwZm9vZHxlbnwwfHx8fDE3OTEyNTcwNjh8MA&ixlib=rb-4.1.0&q=85";


export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const routeFor = (role) => ({ admin: "/admin", kitchen: "/kitchen", store: "/store", planner: "/planner" }[role] || "/cafeteria");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      const home = routeFor(data.user.role);
      const from = location.state?.from;
      navigate(from && from.startsWith(home) ? from : home, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Kitchen" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-900/55 to-orange-900/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/baron-logo.jpg" alt="Baron Kitchen" className="h-14 w-auto rounded-xl bg-white p-1.5 shadow-xl" />
          </div>
          <div>
            <p className="text-xs uppercase font-mono tracking-widest text-amber-300 mb-3">
              Catering Operations Platform
            </p>
            <h1 className="font-heading text-4xl xl:text-5xl font-black leading-tight max-w-md">
              From Base Kitchen to Counter, in perfect sync.
            </h1>
            <p className="mt-4 text-slate-200 max-w-md leading-relaxed">
              Cafeterias order, MIHAN &amp; MANEWADA kitchens cook and dispatch, admins track every kilo and rupee.
            </p>
          </div>
          <p className="text-sm text-slate-300 font-mono">Nagpur · MIHAN · MANEWADA</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8">
            <img src="/baron-logo.jpg" alt="Baron Kitchen" className="h-14 w-auto rounded-xl shadow-sm" />
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="text-muted-foreground mt-1 mb-8">Enter your staff credentials to continue.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. admin"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
            <Button type="submit" className="w-full h-11 text-base" data-testid="login-submit-button" disabled={loading}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
}
